import React from 'react';
import { useTranslation } from 'react-i18next';
import {
  connectCta,
  metricDisplayLabel,
  PERF_MEASURE,
  perfConnectSource,
} from '../data/metrics';
import type { ConnectedSourceId, PerformanceMetric } from '../types';
import { Delta } from './shared/Delta';
import { Icon } from './shared/Icon';
import { ScoreCardHelp } from './shared/ScoreCardHelp';
import { ScorecardNote } from './shared/ScorecardNote';
import { scorecardPeriodDelta, scorecardHeroText } from './shared/Trend';

/** Period total + optional Δ only. Period/compare live in the section head. */
export function ScoreCard({
  m,
  onDrill,
  onConnect,
  comparable = true,
}: {
  m: PerformanceMetric;
  onDrill?: (metricKey: string, outletId?: string) => void;
  onConnect?: (needs: ConnectedSourceId) => void;
  comparable?: boolean;
}) {
  const { t } = useTranslation('performance');
  const clickable = !m.locked && !!onDrill;
  const invert =
    m.key === 'noshow' || m.key === 'cancel' || m.key === 'rejected';
  const periodDelta = comparable ? scorecardPeriodDelta(m) : null;
  const measure = PERF_MEASURE[m.key];
  const noun = measure?.noun;
  const connectSource = perfConnectSource(m.key);
  const baseTip =
    measure?.tip ??
    (m.locked
      ? t('tips.connectPosUnlock')
      : undefined);
  const tip = invert && baseTip
    ? t('tips.lowerIsBetterTip', { tip: baseTip })
    : invert
      ? t('tips.lowerIsBetter')
      : baseTip;

  return (
    <div
      className={`scard${m.locked ? ' locked' : ''}${
        clickable ? ' clickable' : ''
      }`}
      data-hint={clickable ? t('hintDrillDown') : undefined}
      onClick={clickable ? () => onDrill!(m.key) : undefined}
      role={clickable ? 'button' : undefined}
    >
      <div className="sc-head">
        <div className="sc-lab">
          <Icon name={PERF_MEASURE[m.key]?.icon || 'table'} size={14} className="ic" />
          {metricDisplayLabel(m.key, m.label)}
        </div>
        {tip ? <ScoreCardHelp text={tip} /> : null}
      </div>
      {m.locked ? (
        <>
          <div className="sc-ghost">€ ––</div>
          {connectSource && onConnect ? (
            <div
              className="sc-lock-cta"
              onClick={e => {
                e.stopPropagation();
                onConnect(connectSource);
              }}
              role="button"
              tabIndex={0}
              onKeyDown={e => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  e.stopPropagation();
                  onConnect(connectSource);
                }
              }}
            >
              <Icon name="database" size={13} /> {connectCta(connectSource)}
            </div>
          ) : (
            <div className="sc-lock-cta">
              <Icon name="database" size={13} /> {t('scorecard.whyConnectPos')}
            </div>
          )}
        </>
      ) : (
        <div className="sc-hero">
          <div className="sc-hero-val">
            {scorecardHeroText(m)}
            {m.unit ?? ''}
            {noun && <span className="sc-noun">{noun}</span>}
          </div>
          {comparable && (
            <div className="sc-hero-delta">
              <Delta
                value={periodDelta?.value ?? null}
                suffix={periodDelta?.suffix ?? '%'}
                invert={invert}
                size={13}
              />
            </div>
          )}
          <ScorecardNote note={m.note} noteLines={m.noteLines} />
        </div>
      )}
    </div>
  );
}
